// @ts-strict

import { Injectable } from '@angular/core';
import { NgbCalendar, NgbDate, NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { TranslationWidth } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { IDateRange } from '../../statistics-top-menu-new.component';
import { DatePickerService } from '@app/core/services/date-picker/date-picker.service';

const I18N_VALUES = {
  lang: {
    weekdays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    months: ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
  }
};

@Injectable()
export class I18n {
  language = 'lang';
}

@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {
  getWeekdayLabel(weekday: number, width?: TranslationWidth): any {
    return this.getWeekdayShortName(weekday);
  }
  constructor(private datePickerI18n: I18n, private translate: TranslateService) {
    super();
  }

  getWeekdayShortName(weekday: number): string {
    return this.translate.instant('view_translations.short_' + I18N_VALUES[this.datePickerI18n.language as keyof typeof I18N_VALUES].weekdays[weekday - 1]);
  }
  getMonthShortName(month: number): string {
    return I18N_VALUES[this.datePickerI18n.language as keyof typeof I18N_VALUES].months[month - 1];
  }
  getMonthFullName(month: number): string {
    return this.translate.instant('view_translations.' + I18N_VALUES[this.datePickerI18n.language as keyof typeof I18N_VALUES].months[month - 1]);
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day}-${date.month}-${date.year}`;
  }
}

@Injectable()
export class StatisticsDatePickerService {
  private today: NgbDate;
  private previousMonthStart: NgbDate;
  private previousWeekStart: NgbDate;
  private lastYear: NgbDate;
  private previousWeekDay: number;
  private previousMonthLength: number;

  constructor(private readonly calendar: NgbCalendar, private readonly datePickerService: DatePickerService) {
    this.today = this.setToday();
    this.previousMonthStart = this.setPreviousMonthStart();
    this.previousWeekStart = this.setPreviousWeekStart();
    this.lastYear = this.setLastYear();
    this.previousWeekDay = this.setPreviousWeekDay();
    this.previousMonthLength = this.setPreviousMonthLength();
  }

  public setUserDateRanges = (range: { dateFrom: NgbDate; dateTo: NgbDate }): IDateRange[] => {
    return [...this.getDateRanges(), { range: 'user', dateFrom: range.dateFrom, dateTo: range.dateTo }];
  };

  public getDateRanges(): IDateRange[] {
    let dateRanges: IDateRange[];

    dateRanges = [
      {
        range: 'today',
        dateFrom: this.calendar.getToday(),
        dateTo: this.calendar.getToday()
      },
      {
        range: 'yesterday',
        dateFrom: this.calendar.getPrev(this.today, 'd', 1),
        dateTo: this.calendar.getPrev(this.today, 'd', 1)
      },
      {
        range: 'last_week',
        dateFrom: this.calendar.getPrev(this.previousWeekStart, 'd', this.previousWeekDay),
        dateTo: this.calendar.getNext(this.calendar.getPrev(this.previousWeekStart, 'd', this.previousWeekDay), 'd', 6)
      },
      {
        range: 'current_week',
        dateFrom: this.calendar.getPrev(this.today, 'd', this.calendar.getWeekday(this.today) - 1),
        dateTo: this.today
      },
      {
        range: 'last_7_days',
        dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'd', 6),
        dateTo: this.today
      },
      {
        range: 'last_30_days',
        dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'd', 30),
        dateTo: this.today
      },
      {
        range: 'last_month',
        dateFrom: this.calendar.getPrev(this.previousMonthStart, 'd', this.today.day - 1),
        dateTo: this.calendar.getNext(this.calendar.getPrev(this.previousMonthStart, 'd', this.today.day - 1), 'd', this.previousMonthLength)
      },
      {
        range: 'current_month',
        dateFrom: this.calendar.getPrev(this.today, 'd', this.today.day - 1),
        dateTo: this.today
      },
      {
        range: 'this_year',
        dateFrom: new NgbDate(this.today.year, 1, 1),
        dateTo: this.today
      },
      {
        range: 'last_year',
        dateFrom: new NgbDate(this.lastYear.year, 1, 1),
        dateTo: new NgbDate(this.lastYear.year, 12, 31)
      }
    ];

    return dateRanges;
  }

  public getInitialUserRangeForComparePicker = (selectedPeriodDates: IDateRange): { dateFrom: NgbDate; dateTo: NgbDate } => {
    const daysDiff = this.calcDaysDiff(selectedPeriodDates.dateFrom, selectedPeriodDates.dateTo);

    const dateFrom = this.calendar.getPrev(selectedPeriodDates.dateFrom, 'd', daysDiff + 1);

    const dateTo = this.calendar.getPrev(selectedPeriodDates.dateFrom, 'd', 1);

    return {
      dateFrom,
      dateTo
    };
  };

  public checkIfDateStringIsValid(date: string): boolean {
    return moment(date, 'DD/MM/YYYY', true).isValid();
  }

  public checkIfDateRangeExceeded = (date: string, limit: { maxDate: NgbDate; minDate: NgbDate }): boolean => {
    const ngbDate = this.createNgbDateDateFromString(date);

    return ngbDate.before(limit.minDate) || ngbDate.after(limit.maxDate);
  };

  public createNgbDateDateFromString(date: string): NgbDate {
    const momentDate = moment(date, 'DD/MM/YYYY');

    return new NgbDate(momentDate.year(), momentDate.month() + 1, momentDate.date());
  }

  public calcDaysDiff(dateFrom: NgbDate, dateTo: NgbDate): number {
    const fromDate: Date = this.createDateFromNgbDate(dateFrom);
    const toDate: Date = this.createDateFromNgbDate(dateTo);

    const daysDiff = Math.floor(Math.abs(<any>fromDate - <any>toDate) / (1000 * 60 * 60 * 24));

    return daysDiff;
  }

  public getCurrentMonthStartDay = (): NgbDate => {
    return this.calendar.getPrev(this.today, 'd', this.today.day - 1);
  };

  private setToday = (): NgbDate => {
    return this.calendar.getToday();
  };

  private setPreviousMonthStart = (): NgbDate => {
    return this.calendar.getPrev(this.today, 'm', 1);
  };

  private setPreviousWeekStart = (): NgbDate => {
    return this.calendar.getPrev(this.today, 'd', 7);
  };

  private setLastYear = (): NgbDate => {
    return this.calendar.getPrev(this.today, 'y', 1);
  };

  private setPreviousWeekDay = (): number => {
    return this.calendar.getWeekday(this.previousWeekStart) - 1;
  };

  private setPreviousMonthLength = (): number => {
    return this.datePickerService.getNumberOfDaysMonth(this.today.month - 1, this.today.year) - 1;
  };

  private createDateFromNgbDate(ngbDate: NgbDate): Date {
    const date: Date = new Date(Date.UTC(ngbDate.year, ngbDate.month - 1, ngbDate.day));
    return date;
  }
}
